import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Crown, Gift, Loader2, PoundSterling, Send } from "lucide-react";
import "../styles/landing.css";

// The partner endpoints live on the main Thryvin' app API (they read the same
// database RevenueCat writes to), NOT the landing-page backend.
const APP_API = process.env.REACT_APP_APP_API || "https://thryvin-production-fbdd.up.railway.app";

// The standard deal (Jake, 2026-10-12). The page asks the API for the live
// numbers and only falls back to these if the API can't be reached.
const FALLBACK_DEAL = { ratePercent: 30, months: 12, customerTrialDays: 30, holdDays: 60, minimumPayout: 20, lifetimePro: true };

// One paying monthly member: £7.99, less VAT, less the 15% store fee ≈ £5.66 to
// Thryvin'. 30% of that is about £1.70 a month. Used for the worked example only.
const NET_PER_MONTHLY_MEMBER = 5.66;

const money = (n, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(n ?? 0);
// "£17" rather than "£17.00" for round example figures.
const roundMoney = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2 }).format(n);

function readHash() {
  const hash = window.location.hash.replace(/^#/, "");
  if (hash === "demo") return { demo: true, key: null };
  const key = new URLSearchParams(hash).get("k") || new URLSearchParams(window.location.search).get("k");
  return { demo: false, key };
}

export default function Affiliates() {
  const [view, setView] = useState(() => readHash());
  const [deal, setDeal] = useState(FALLBACK_DEAL);

  useEffect(() => {
    const onHash = () => setView(readHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    fetch(`${APP_API}/api/affiliate/deal`)
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => { if (payload?.ok && payload.data) setDeal({ ...FALLBACK_DEAL, ...payload.data }); })
      .catch(() => {});
  }, []);

  return (
    <div className="landing-page partners-page">
      <header className="pt-topbar">
        <Link to="/" className="pt-logo"><img src="/logo-cropped.png" alt="Thryvin'" /></Link>
        {view.key || view.demo
          ? <Link to="/partners" className="pt-toplink" onClick={() => { window.location.hash = ""; }}>About the programme</Link>
          : <a href="#apply" className="pt-toplink">Apply</a>}
      </header>
      {view.key || view.demo ? <Dashboard dashKey={view.key} demo={view.demo} deal={deal} /> : <Pitch deal={deal} />}
    </div>
  );
}

/* ───────────────────────────── The pitch ───────────────────────────── */

function Pitch({ deal }) {
  // Round to whole pence first so the three example rows add up exactly.
  const perMember = Math.round(NET_PER_MONTHLY_MEMBER * deal.ratePercent) / 100;
  return (
    <main className="pt-wrap">
      <section className="pt-hero">
        <p className="aff-eyebrow">Thryvin&apos; Partners</p>
        <h1 className="pt-title">Share Thryvin&apos;.<br /><span className="gradient-text">Earn {deal.ratePercent}% for a year.</span></h1>
        <p className="pt-lead">
          For coaches, clubs and creators whose people want to train more consistently.
          They get {deal.customerTrialDays} days of Pro free. You earn every month they stay.
        </p>
        <div className="pt-hero-ctas">
          <a href="#apply" className="aff-cta">Apply in a minute</a>
          <a href="#demo" className="pt-ghost">See an example dashboard</a>
        </div>
      </section>

      <section className="pt-deal" aria-label="The deal">
        <DealCard icon={Gift} who="Your people get" big={`${deal.customerTrialDays} days free`} small="of Thryvin' Pro when they sign up with your code." />
        <DealCard icon={PoundSterling} who="You get" big={`${deal.ratePercent}% for ${deal.months} months`} small="of what Thryvin' receives from everyone who pays after using your code." highlight />
        <DealCard icon={Crown} who="You also get" big="Pro free for life" small="So you can use it properly and talk about it honestly." />
      </section>

      <section className="pt-example">
        <h2>What that looks like</h2>
        <div className="pt-example-rows">
          <ExampleRow label="1 person paying monthly" value={`about ${roundMoney(perMember)} a month`} />
          <ExampleRow label="10 people paying monthly" value={`about ${roundMoney(perMember * 10)} a month`} />
          <ExampleRow label="10 people, kept for a year" value={`about ${roundMoney(perMember * 10 * deal.months)}`} strong />
        </div>
        <p className="pt-note">
          Based on the £7.99 monthly plan, after VAT and the app store&apos;s fee. Examples, not a promise:
          you earn on what people actually pay.
        </p>
      </section>

      <section className="pt-steps">
        <h2>How it works</h2>
        <ol>
          <li><span>1</span><div><strong>Apply below.</strong> I read every application myself and reply by email.</div></li>
          <li><span>2</span><div><strong>Get your code.</strong> Something simple like <code>SARAH</code>, plus a link that shows people how to use it.</div></li>
          <li><span>3</span><div><strong>Share it however suits you.</strong> A post, your stories, your club chat, or just telling clients.</div></li>
          <li><span>4</span><div><strong>Get paid monthly.</strong> Your private dashboard shows sign-ups, paying members and what you&apos;re owed.</div></li>
        </ol>
      </section>

      <ApplyForm />

      <section className="pt-faq">
        <h2>Questions</h2>
        <Faq q="Who counts as my referral?">
          Anyone who types your code when they create their Thryvin&apos; account. It has to be at sign-up:
          it can&apos;t be added afterwards, so remind people to use it.
        </Faq>
        <Faq q="When do I get paid?">
          Monthly, by bank transfer, once you&apos;re owed at least £{deal.minimumPayout}. Smaller amounts roll over.
          Each payment clears for {deal.holdDays} days first, because people can ask Apple or Google for a refund.
        </Faq>
        <Faq q="What doesn't count?">
          Free trials (until they turn into a payment), refunded payments, test purchases and your own account.
          If someone is refunded after you&apos;ve been paid, it comes off your next payout.
        </Faq>
        <Faq q="Do I have to post a certain amount?">
          No targets and nothing to buy. Share it when it fits.
        </Faq>
        <Faq q="What do I need to say when I post?">
          Put <strong>#ad</strong> at the start of anything about Thryvin&apos;. UK advertising rules (the ASA) apply
          to both of us. Talk about your own experience, and please don&apos;t promise results like weight loss or injury recovery.
        </Faq>
      </section>

      <section className="pt-rules">
        <h3>The small print</h3>
        <ul>
          <li>No self-referrals, no paid ads on the Thryvin&apos; name, no spam, and no rewards in exchange for app store reviews.</li>
          <li>Codes that break these rules can be paused. Anything you&apos;ve already earned fairly is still paid.</li>
          <li>Either of us can end the partnership at any time.</li>
        </ul>
        <p className="aff-smallprint">
          Questions? support@thryvin.app · <Link to="/">Back to Thryvin&apos;</Link>
        </p>
      </section>
    </main>
  );
}

function DealCard({ icon: Icon, who, big, small, highlight }) {
  return (
    <div className={`pt-deal-card ${highlight ? "is-highlight" : ""}`}>
      <Icon size={22} className="aff-ic" aria-hidden="true" />
      <span className="pt-deal-who">{who}</span>
      <strong className="pt-deal-big">{big}</strong>
      <span className="pt-deal-small">{small}</span>
    </div>
  );
}

function ExampleRow({ label, value, strong }) {
  return (
    <div className={`pt-example-row ${strong ? "is-strong" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="pt-faq-item">
      <summary>{q}</summary>
      <p>{children}</p>
    </details>
  );
}

/* ─────────────────────────── Application form ─────────────────────────── */

function ApplyForm() {
  const [form, setForm] = useState({ name: "", email: "", profile: "", audience: "", message: "", website: "" });
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${APP_API}/api/affiliate/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Something went wrong. Please email support@thryvin.app instead.");
      setState("sent");
    } catch (err) {
      setError(err.message || "Couldn't send. Please email support@thryvin.app instead.");
      setState("error");
    }
  }

  return (
    <section className="pt-apply" id="apply">
      <h2>Apply</h2>
      {state === "sent" ? (
        <div className="pt-sent" role="status">
          <Check size={28} aria-hidden="true" />
          <strong>Thanks, that&apos;s arrived.</strong>
          <span>I&apos;ll reply to {form.email} either way. A copy of your application is on its way to your inbox.</span>
        </div>
      ) : (
        <form onSubmit={submit} className="pt-form" noValidate={false}>
          <p className="pt-form-intro">Takes about a minute. Nothing is agreed until we&apos;ve both said yes.</p>
          <div className="pt-form-row">
            <label htmlFor="apply-name">Your name<input id="apply-name" required maxLength={100} value={form.name} onChange={set("name")} autoComplete="name" /></label>
            <label htmlFor="apply-email">Email<input id="apply-email" type="email" required maxLength={320} value={form.email} onChange={set("email")} autoComplete="email" /></label>
          </div>
          <label htmlFor="apply-profile">Where you post or coach<input id="apply-profile" required maxLength={300} placeholder="Instagram, TikTok, website or club page" value={form.profile} onChange={set("profile")} /></label>
          <label htmlFor="apply-audience">Who your people are <em>(optional)</em><input id="apply-audience" maxLength={300} placeholder="e.g. beginners at my gym, a Saturday run club, gym-goers in their 30s" value={form.audience} onChange={set("audience")} /></label>
          <label htmlFor="apply-message">Anything else <em>(optional)</em><textarea id="apply-message" rows={3} maxLength={2000} value={form.message} onChange={set("message")} /></label>
          {/* Honeypot: hidden from people, irresistible to bots. */}
          <input className="pt-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} name="website" />
          <button type="submit" className="aff-cta pt-submit" disabled={state === "sending"}>
            {state === "sending" ? <Loader2 size={18} className="spinner" /> : <Send size={18} />}
            <span>{state === "sending" ? "Sending…" : "Send application"}</span>
          </button>
          {state === "error" && <p className="aff-error" role="alert">{error}</p>}
        </form>
      )}
    </section>
  );
}

/* ───────────────────────────── Dashboard ───────────────────────────── */

const DEMO_STATS = {
  name: "Sarah", code: "SARAH", shareUrl: "https://thryvin.app/refer/SARAH",
  linkClicks: 214, signups: 23, referredCustomers: 9,
  money: { currency: "GBP", owedNow: 24.65, refundToDeduct: 0, clearing: 15.3, paidOut: 40.8, earnedTotal: 80.75, readyToPay: true, otherCurrencies: [] },
  payoutDetails: null,
};

function normaliseStats(raw, deal) {
  // Newer API: `deal` + `money`. Older API: rewardType + balances. Both work.
  const cash = raw.money || (raw.rewardType === "cash" && raw.rewardsEnabled
    ? { currency: raw.currency || "GBP", owedNow: raw.dueNow, clearing: raw.onHold, paidOut: raw.paidOut, earnedTotal: raw.earnedTotal, readyToPay: raw.dueNow >= deal.minimumPayout, otherCurrencies: [] }
    : null);
  return {
    name: raw.name, code: raw.code,
    shareUrl: raw.shareUrl || `https://thryvin.app/refer/${encodeURIComponent(raw.code)}`,
    linkClicks: raw.linkClicks ?? 0, signups: raw.signups ?? 0, referredCustomers: raw.referredCustomers ?? 0,
    money: cash,
    payoutDetails: raw.payoutDetails || null,
    deal: raw.deal || null,
    otherArrangement: !cash ? raw.rewardDescription : null,
  };
}

function Dashboard({ dashKey, demo, deal }) {
  const [state, setState] = useState(demo ? "done" : "loading");
  const [stats, setStats] = useState(demo ? { ...DEMO_STATS, deal } : null);
  const [error, setError] = useState("");
  const migrated = useRef(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await fetch(`${APP_API}/api/affiliate/stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: dashKey }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.error || "That link isn't valid. Ask Jake to resend it.");
      setStats(normaliseStats(data.data, deal));
      setState("done");
    } catch (err) {
      setError(err.message || "Couldn't reach the server. Try again in a minute.");
      setState("error");
    }
  }, [dashKey, deal]);

  useEffect(() => {
    if (demo) { setStats({ ...DEMO_STATS, deal }); setState("done"); return; }
    // Old ?k= links: move the key behind # so it never reaches server logs.
    if (!migrated.current && !window.location.hash && dashKey) {
      migrated.current = true;
      window.history.replaceState(null, "", `${window.location.pathname}#k=${dashKey}`);
    }
    if (dashKey) load();
  }, [dashKey, demo, deal, load]);

  if (state === "loading") return <main className="pt-wrap pt-dash"><Loader2 className="spinner aff-ic" size={30} /></main>;
  if (state === "error") return <main className="pt-wrap pt-dash"><p className="aff-error">{error}</p></main>;

  const d = stats.deal || deal;
  const m = stats.money;
  const toGo = m ? Math.max(0, d.minimumPayout - m.owedNow) : 0;
  const refund = m?.refundToDeduct || 0;

  return (
    <main className="pt-wrap pt-dash">
      {demo && <p className="pt-demo-banner">Example dashboard with made-up numbers. Yours will look like this.</p>}
      <div className="pt-dash-head">
        <div>
          <p className="aff-eyebrow">Your partner dashboard</p>
          <h1>Hi {stats.name}</h1>
        </div>
        <CopyLink code={stats.code} url={stats.shareUrl} />
      </div>

      {m ? (
        <section className="pt-owed">
          <span className="pt-owed-label">Owed to you</span>
          <strong className="pt-owed-big">{money(m.owedNow, m.currency)}</strong>
          <span className="pt-owed-sub">
            {refund > 0
              ? `A ${money(refund, m.currency)} refund comes off your next payout.`
              : m.readyToPay ? "Paid in the next monthly payout." : `Payouts start at £${d.minimumPayout}. ${money(toGo)} to go.`}
          </span>
          <div className="pt-owed-rows">
            <div><span>Clearing</span><strong>{money(m.clearing, m.currency)}</strong><small>Each payment clears for {d.holdDays} days</small></div>
            <div><span>Paid to you so far</span><strong>{money(m.paidOut, m.currency)}</strong></div>
          </div>
        </section>
      ) : (
        <section className="pt-owed"><span className="pt-owed-label">Your arrangement</span><p className="pt-owed-sub">{stats.otherArrangement}</p></section>
      )}

      {m && (
        <PayoutDetails
          dashKey={dashKey}
          demo={demo}
          saved={stats.payoutDetails}
          onSaved={(payoutDetails) => setStats((current) => ({ ...current, payoutDetails }))}
        />
      )}

      <section className="pt-funnel" aria-label="Your numbers">
        <Funnel n={stats.linkClicks} label="visited your share link" />
        <Funnel n={stats.signups} label="signed up with your code" />
        <Funnel n={stats.referredCustomers} label="are paying members" strong />
      </section>

      {m && (
        <p className="pt-dash-deal">
          Your deal: {d.ratePercent}% of what each paying member brings in, for their first {d.months} months
          {d.lifetimePro ? ", plus Pro free for life" : ""}. Remind people to type <strong>{stats.code}</strong> when they sign up.
        </p>
      )}
      <p className="pt-note">This page is private to you. Bookmark it, and don&apos;t share the address.</p>
    </main>
  );
}

/** Where to send the money: UK bank or PayPal, asked once, shown back masked. */
function PayoutDetails({ dashKey, demo, saved, onSaved }) {
  const [editing, setEditing] = useState(!saved);
  const [form, setForm] = useState({ method: "bank", accountName: "", sortCode: "", accountNumber: "", paypalEmail: "" });
  const [state, setState] = useState("idle"); // idle | saving | error
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setState("saving");
    setError("");
    const body = form.method === "bank"
      ? { method: "bank", accountName: form.accountName, sortCode: form.sortCode, accountNumber: form.accountNumber }
      : { method: "paypal", paypalEmail: form.paypalEmail };
    try {
      if (demo) {
        onSaved(form.method === "bank"
          ? { method: "bank", summary: `${form.accountName || "Sarah Example"} · account ending ${(form.accountNumber || "12345678").slice(-4)}` }
          : { method: "paypal", summary: "PayPal · sa•••@example.com" });
      } else {
        const res = await fetch(`${APP_API}/api/affiliate/payout-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: dashKey, ...body }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.ok) throw new Error(data?.error || "Couldn't save that. Please try again.");
        onSaved(data.data.payoutDetails);
      }
      // Don't keep the typed numbers around once they're saved.
      setForm({ method: form.method, accountName: "", sortCode: "", accountNumber: "", paypalEmail: "" });
      setEditing(false);
      setState("idle");
    } catch (err) {
      setError(err.message || "Couldn't save that. Please try again.");
      setState("error");
    }
  }

  if (saved && !editing) {
    return (
      <section className="pt-payout">
        <div>
          <span className="pt-owed-label">We pay you to</span>
          <p className="pt-payout-summary">{saved.summary}</p>
        </div>
        <button type="button" className="pt-payout-change" onClick={() => setEditing(true)}>Change</button>
      </section>
    );
  }

  return (
    <section className="pt-payout is-form">
      <span className="pt-owed-label">Where should we send your money?</span>
      <p className="pt-payout-intro">Add this once. Only you and Jake can see it, and it&apos;s stored encrypted.</p>
      <form onSubmit={submit} className="pt-form">
        <div className="pt-payout-methods" role="radiogroup" aria-label="How you'd like to be paid">
          <label className={form.method === "bank" ? "is-on" : ""}><input type="radio" name="payout-method" value="bank" checked={form.method === "bank"} onChange={set("method")} />UK bank transfer</label>
          <label className={form.method === "paypal" ? "is-on" : ""}><input type="radio" name="payout-method" value="paypal" checked={form.method === "paypal"} onChange={set("method")} />PayPal</label>
        </div>
        {form.method === "bank" ? (
          <>
            <label htmlFor="payout-name">Name on the account<input id="payout-name" required maxLength={100} autoComplete="name" value={form.accountName} onChange={set("accountName")} /></label>
            <div className="pt-form-row">
              <label htmlFor="payout-sort">Sort code<input id="payout-sort" required inputMode="numeric" placeholder="12-34-56" maxLength={8} pattern="[0-9]{2}-?[0-9]{2}-?[0-9]{2}" value={form.sortCode} onChange={set("sortCode")} /></label>
              <label htmlFor="payout-account">Account number<input id="payout-account" required inputMode="numeric" placeholder="12345678" maxLength={8} pattern="[0-9]{8}" value={form.accountNumber} onChange={set("accountNumber")} /></label>
            </div>
          </>
        ) : (
          <label htmlFor="payout-paypal">PayPal email<input id="payout-paypal" type="email" required maxLength={320} autoComplete="email" value={form.paypalEmail} onChange={set("paypalEmail")} /></label>
        )}
        <div className="pt-payout-actions">
          <button type="submit" className="aff-cta" disabled={state === "saving"}>
            {state === "saving" ? <Loader2 size={18} className="spinner" /> : <Check size={18} />}
            <span>{state === "saving" ? "Saving…" : "Save"}</span>
          </button>
          {saved && <button type="button" className="pt-payout-change" onClick={() => setEditing(false)}>Cancel</button>}
        </div>
        {state === "error" && <p className="aff-error" role="alert">{error}</p>}
      </form>
    </section>
  );
}

function Funnel({ n, label, strong }) {
  return (
    <div className={`pt-funnel-step ${strong ? "is-strong" : ""}`}>
      <strong>{n}</strong>
      <span>{label}</span>
    </div>
  );
}

function CopyLink({ code, url }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(url); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } catch { /* user can still select the text */ }
  }
  return (
    <div className="pt-copy">
      <span>Your code <strong>{code}</strong></span>
      <button type="button" onClick={copy}>
        {copied ? <Check size={16} /> : <Copy size={16} />}
        <span>{copied ? "Link copied" : "Copy your share link"}</span>
      </button>
    </div>
  );
}
